import "../styles/people-families.css";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import AdminLayout from "../components/AdminLayout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import Pagination from "../components/Pagination";
import {
  Home, Search, Filter, X, Upload,
  AlertTriangle, RefreshCw
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ImportWizard from "../components/ImportWizard";
import HouseholdReviewPanel from "../components/people/HouseholdReviewPanel";
import { findDuplicateHouseholds, getHouseholdReadiness } from "../components/people/householdOperations";
import PeopleWorkspaceNav from "../components/people/PeopleWorkspaceNav";

const EmbeddedLayout = ({ children }) => <>{children}</>;

const AdminHouseholds = ({ embedded = false, routeBase = "/admin/people" }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [households, setHouseholds] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [programFilter, setProgramFilter] = useState("all");
  const [householdView, setHouseholdView] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchData();
  }, [token, page, pageSize]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const headers = { Authorization: `Bearer ${token}` };
      const [householdsRes, programsRes] = await Promise.all([
        axios.get(`${API}/admin/households`, { 
          headers,
          params: { page, page_size: pageSize, search: searchTerm || undefined }
        }),
        axios.get(`${API}/programs`, { headers, params: { page: 1, page_size: 100 } })
      ]);
      
      // Handle paginated response
      if (householdsRes.data.items) {
        setHouseholds(householdsRes.data.items);
        setTotal(householdsRes.data.total);
        setTotalPages(householdsRes.data.total_pages);
      } else {
        setHouseholds(Array.isArray(householdsRes.data) ? householdsRes.data : []);
        setTotal(householdsRes.data.length || 0);
        setTotalPages(1);
      }
      
      // Handle programs response
      if (programsRes.data.items) {
        setPrograms(programsRes.data.items);
      } else {
        setPrograms(Array.isArray(programsRes.data) ? programsRes.data : []);
      }
    } catch (error) {
      console.error("Error fetching households:", error);
      setLoadError(true);
      toast.error("Failed to load households");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchData();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const getPendingBalance = (household) => household.registrations
    ?.filter((registration) => registration.payment_status !== "paid")
    .reduce((sum, registration) => sum + (Number(registration.amount) || 0), 0) || 0;

  const needsContactInfo = (household) => !household.members?.some(
    (member) => member.email || member.phone
  );

  const operationalHouseholds = households.map((household) => ({
    ...household,
    readiness: getHouseholdReadiness(household),
  }));
  const duplicateCandidates = findDuplicateHouseholds(operationalHouseholds);

  // Client-side operational views supplement the server-backed search.
  const filteredHouseholds = operationalHouseholds.filter((household) => {
    const matchesProgram = programFilter === "all" ||
      household.registrations?.some((registration) => registration.program_id === programFilter);
    const matchesView = householdView === "all" ||
      (householdView === "balance" && getPendingBalance(household) > 0) ||
      (householdView === "missing-contact" && needsContactInfo(household)) ||
      (householdView === "readiness" && !household.readiness.ready);
    return matchesProgram && matchesView;
  });

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-900"></div>
        </div>
      </PageLayout>
    );
  }

  if (loadError) {
    return (
      <PageLayout title="People & Households">
        <div className="rounded-3xl border border-red-200 bg-white px-6 py-16 text-center" data-testid="households-error-state">
          <AlertTriangle className="mx-auto h-10 w-10 text-red-500" />
          <h1 className="mt-4 text-xl font-semibold text-slate-950">Households could not be loaded</h1>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">Your filters are preserved. Check the connection and try again.</p>
          <Button onClick={fetchData} className="mt-5 rounded-xl bg-teal-700 hover:bg-teal-800">
            <RefreshCw className="mr-2 h-4 w-4" /> Try again
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="people-families space-y-5" data-testid="admin-households-page">
        <EditorialPageHeader
          title="Households"
          description={`${total} household records. Find the family behind each player.`}
          headingId="households-heading"
          aside={<Button
            onClick={() => setImportWizardOpen(true)}
            className="platform-action platform-action-primary"
            data-testid="import-households-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>}
        />

        {embedded && (
          <nav className="pf-tabs" aria-label="People workspace" data-testid="people-portal-navigation">
            <Button variant="outline" onClick={() => navigate(`${routeBase}/directory`)}>Directory</Button>
            <Button variant="secondary" aria-current="page" onClick={() => navigate(`${routeBase}/households`)}>Households</Button>
            <Button variant="outline" onClick={() => navigate(`${routeBase}/enrollments`)}>Enrollment history</Button>
          </nav>
        )}

        <PeopleWorkspaceNav active="households" />

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Views on this page</span>
            <button type="button" onClick={() => setHouseholdView("all")} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${householdView === "all" ? "bg-slate-950 text-white" : "border border-slate-200 text-slate-600"}`}>All households</button>
            <button type="button" onClick={() => setHouseholdView("balance")} className={`rounded-full border border-amber-200 px-3 py-1.5 text-xs font-semibold text-amber-800 ${householdView === "balance" ? "bg-amber-100" : "bg-amber-50"}`} data-testid="households-balance-view">
              Balance due · {households.filter((household) => getPendingBalance(household) > 0).length}
            </button>
            <button type="button" onClick={() => setHouseholdView("missing-contact")} className={`rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 ${householdView === "missing-contact" ? "bg-slate-100" : "bg-white"}`} data-testid="households-contact-view">
              Missing contact · {households.filter(needsContactInfo).length}
            </button>
            <button type="button" onClick={() => setHouseholdView("readiness")} className={`rounded-full border border-teal-200 px-3 py-1.5 text-xs font-semibold text-teal-800 ${householdView === "readiness" ? "bg-teal-100" : "bg-teal-50"}`} data-testid="households-readiness-view">
              Player readiness · {operationalHouseholds.filter((household) => !household.readiness.ready).length}
            </button>
          </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by household or player name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 h-11 rounded-2xl border-slate-200"
              data-testid="household-search"
            />
          </div>
          <Button
            variant="outline"
            onClick={handleSearch}
            className="rounded-2xl border-slate-200 h-11"
          >
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="rounded-2xl border-slate-200 h-11"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {programFilter !== "all" && (
              <span className="ml-2 w-5 h-5 bg-slate-900 text-white text-xs rounded-full flex items-center justify-center">1</span>
            )}
          </Button>
        </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white border border-slate-200/60 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-700">Filter by Program</span>
              {programFilter !== "all" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setProgramFilter("all")}
                  className="text-slate-500 h-8 rounded-xl"
                >
                  <X className="w-3 h-3 mr-1" /> Clear
                </Button>
              )}
            </div>
            <Select value={programFilter} onValueChange={setProgramFilter}>
              <SelectTrigger className="rounded-2xl border-slate-200">
                <SelectValue placeholder="All Programs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Programs</SelectItem>
                {programs.map(program => (
                  <SelectItem key={program.program_id} value={program.program_id}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Households List */}
        {filteredHouseholds.length > 0 ? (
          <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden">
            <table className="pf-table" aria-label="Households" role="table">
            <thead role="rowgroup"><tr className="pf-household-head" role="row"><th scope="col" id="pf-household-0">Household</th><th scope="col" id="pf-household-1">Primary contact</th><th scope="col" id="pf-household-2">Contact</th><th scope="col" id="pf-household-3">Members</th></tr></thead>
            <tbody className="divide-y divide-slate-100" role="rowgroup">
              {filteredHouseholds.map((household) => {
                const primary = household.members?.find(member => member.is_primary);
                return <tr role="row" key={household.household_id} className="pf-household-row">
                  <td role="cell" headers="pf-household-0"><button type="button" data-testid={`household-${household.household_id}`} onClick={() => navigate(routeBase.startsWith("/org/") ? `${routeBase}/households/${household.household_id}` : `/admin/households/${household.household_id}`)}>{household.name}</button>
                    {household.city && <small>{household.city}, {household.state}</small>}
                    {getPendingBalance(household) > 0 && <small>${getPendingBalance(household).toLocaleString()} due</small>}
                    {needsContactInfo(household) && <small>Missing contact</small>}
                    {!household.readiness.ready && <small>Player readiness needs follow-up</small>}
                  </td>
                  <td role="cell" headers="pf-household-1"><span aria-hidden="true" className="pf-mobile-label">Primary contact</span>{household.primary_contact_name || (primary ? [primary.first_name, primary.last_name].filter(Boolean).join(" ") : "Not designated")}</td>
                  <td role="cell" headers="pf-household-2"><span aria-hidden="true" className="pf-mobile-label">Contact</span>{household.primary_contact_email || primary?.email || household.members?.find(member => member.email)?.email || "Email not provided"}<small>{household.primary_contact_phone || primary?.phone || ""}</small></td>
                  <td role="cell" headers="pf-household-3"><span aria-hidden="true" className="pf-mobile-label">Members</span>{household.members?.length || 0} adults<small>{household.players?.length || 0} players</small></td>
                </tr>;
              })}
            </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200/60 rounded-2xl p-12 text-center">
            <Home className="w-12 h-12 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Households Found</h3>
            <p className="text-slate-500">
              {searchTerm || programFilter !== "all" 
                ? "Try adjusting your search or filters" 
                : "Households will appear here when families register for your programs"}
            </p>
          </div>
        )}
        <details className="pf-review"><summary>Household readiness and duplicate review</summary><HouseholdReviewPanel households={operationalHouseholds} duplicateCandidates={duplicateCandidates} onOpenHousehold={(householdId) => navigate(routeBase.startsWith("/org/") ? `${routeBase}/households/${householdId}` : `/admin/households/${householdId}`)} /></details>
      </div>

      {/* Import Wizard */}
      <ImportWizard 
        open={importWizardOpen} 
        onOpenChange={setImportWizardOpen}
        initialImportType="households"
        onImportComplete={() => {
          fetchData();
          toast.success("Import completed successfully");
        }}
      />
    </PageLayout>
  );
};

export default AdminHouseholds;
